import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowUpDown } from "lucide-react";

const docTime = (d) => {
  if (d?.created_at) { const t = Date.parse(d.created_at); if (!isNaN(t)) return t; }
  if (typeof d?._id === "string" && d._id.length >= 8) { const t = parseInt(d._id.substring(0, 8), 16); if (!isNaN(t)) return t * 1000; }
  return 0;
};
const str = (v) => String(v ?? "").trim().toLowerCase();
const GETTERS = {
  geo: (d) => `${str(d.nation)} ${str(d.region)} ${str(d.city)}`.trim(),
  created_at: docTime,
};

export function useSortOptions(module) {
  const [options, setOptions] = useState([]);
  const [catalog, setCatalog] = useState({});
  const reload = useCallback(() => {
    if (!module) return;
    api.get(`/sort-options?module=${module}`)
      .then(({ data }) => { setOptions(data.options || []); setCatalog(data.catalog || {}); })
      .catch(() => {});
  }, [module]);
  useEffect(() => { reload(); }, [reload]);
  return { options, catalog, reload };
}

/** id dell'opzione "data inserimento · dal più recente" (o la prima disponibile). */
export const pickRecentId = (options) =>
  (options.find((o) => o.field === "created_at" && o.dir === "desc") || options[0])?.id || "";

export function sortRows(rows, optionId, options, catalog = {}) {
  const opt = options.find((o) => o.id === optionId) || options.find((o) => o.field === "created_at" && o.dir === "desc");
  const list = [...rows];
  if (!opt) return list.sort((a, b) => docTime(b) - docTime(a));
  const type = catalog?.[opt.field]?.type || (opt.field === "created_at" ? "date" : "string");
  const get = GETTERS[opt.field] || ((d) => d?.[opt.field]);
  const sign = opt.dir === "desc" ? -1 : 1;
  const byName = (a, b) => str(a?.name).localeCompare(str(b?.name), "it");
  let cmp;
  if (type === "date" || type === "number") {
    cmp = (a, b) => (Number(get(a)) || 0) - (Number(get(b)) || 0);
  } else if (type === "enum") {
    const order = (opt.values_order?.length ? opt.values_order : (catalog?.[opt.field]?.values || [])).map(str);
    const rank = (d) => { const i = order.indexOf(str(get(d) || "nuovo")); return i < 0 ? order.length : i; };
    cmp = (a, b) => rank(a) - rank(b) || str(get(a)).localeCompare(str(get(b)), "it");
  } else {
    cmp = (a, b) => str(get(a)).localeCompare(str(get(b)), "it");
  }
  return list.sort((a, b) => sign * cmp(a, b) || byName(a, b));
}

export function SortSelect({ options, value, onChange, testid = "sort-select", className = "h-9 w-56 text-xs", label = null, placeholder = "Ordina per…" }) {
  const current = options.some((o) => o.id === value) ? value : pickRecentId(options);
  const trigger = (
    <Select value={current} onValueChange={onChange}>
      <SelectTrigger className={className} data-testid={testid}>
        <ArrowUpDown className="mr-1.5 h-3.5 w-3.5 shrink-0 text-brand" />
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-[320px]">
        {options.map((o) => <SelectItem key={o.id} value={o.id} data-testid={`sort-option-${o.id}`}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
  if (!label) return trigger;
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {trigger}
    </div>
  );
}
